import { Alert, Box, Text, Title } from '@mantine/core'

import { useTranslation } from '@flecto/translations'
import { formatDateString } from '@flecto/utils'

import type { ScheduleBooking } from '../../types/ScheduleBooking'
import type { ScheduleDay } from '../../types/ScheduleDay'
import { BookingCard } from '../BookingCard/BookingCard'

export function ScheduleList({
  calendar,
}: {
  calendar: Map<string, ScheduleDay>
}) {
  const { t } = useTranslation('saas', { keyPrefix: 'schedule' })

  const getHour = (row: ScheduleBooking, type: 'end' | 'start') =>
    type === 'start'
      ? formatDateString({ date: row.start_at, variant: 'time-brief' })
      : formatDateString({ date: row.end_at, variant: 'time-brief' })

  return (
    <Box mt='md'>
      {new Array(calendar.size).fill(0).map((_, key) => {
        const data = calendar.get(String(key))

        if (!data) {
          return <></>
        }

        const starts = (data?.rows ?? [])
          .filter((item: ScheduleBooking) => {
            return (
              formatDateString({
                date: item.start_at,
                variant: 'date-brief',
              }) ===
              formatDateString({
                date: data.currentDateStart ?? '',
                variant: 'date-brief',
              })
            )
          })
          .map((item: ScheduleBooking) => {
            const hour = getHour(item, 'start')

            return {
              ...item,
              operation: 'start' as const,
              hour,
              hourTimestamp: new Date(`1970/01/01 ${hour}`).getTime(),
            }
          })

        const ends = (data?.rows ?? [])
          .filter((item: ScheduleBooking) => {
            return (
              formatDateString({
                date: item.end_at,
                variant: 'date-brief',
              }) ===
              formatDateString({
                date: data.currentDateEnd ?? '',
                variant: 'date-brief',
              })
            )
          })
          .map((item: ScheduleBooking) => {
            const hour = getHour(item, 'end')

            return {
              ...item,
              operation: 'end' as const,
              hour,
              hourTimestamp: new Date(`1970/01/01 ${hour}`).getTime(),
            }
          })

        const bookings = [...ends, ...starts].sort(
          (a, b) => a.hourTimestamp - b.hourTimestamp
        )

        return (
          <Box key={`${Math.random()}-${String(key)}`} mb='xl'>
            <Box mb='md'>
              <Title
                order={5}
                c={data.isToday ? 'brand' : undefined}
                tt='capitalize'
              >
                {data.label}
              </Title>
            </Box>
            {bookings.length > 0 &&
              bookings.map((booking, key) => (
                <BookingCard
                  key={`booking-card-${booking.id}-${key}`}
                  booking={booking}
                  mb='md'
                />
              ))}
            {!bookings.length && (
              <Alert color='gray.4' p='lg'>
                <Text c='dimmed'>{t('no_booking_schedule')}</Text>
              </Alert>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
